import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowRight, Play, ShieldCheck, Percent, FileText } from "lucide-react";
import FakeQR from "@/components/FakeQR";
import { EASE } from "@/components/Reveal";
import { scrollToTarget } from "@/lib/scroll";

function LineReveal({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function PayCard() {
  return (
    <div className="relative rounded-2xl border border-line bg-panel/90 p-6 shadow-[0_40px_120px_-40px_rgba(20,241,149,0.15)] backdrop-blur-xl sm:p-7">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="ping-dot h-2 w-2 rounded-full bg-sgreen" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-mut">
            Payment request
          </span>
        </div>
        <span className="rounded-full border border-sgreen/30 bg-sgreen/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-sgreen">
          Live
        </span>
      </div>

      <p className="mt-7 font-mono text-4xl font-semibold tracking-tight text-white sm:text-5xl">
        249.00
        <span className="ml-2 text-base font-medium text-sgreen">USDC</span>
      </p>
      <p className="mt-3 font-mono text-xs text-mut">
        To: 9xQeW...vVuK · Memo: Order #1042
      </p>

      <div className="mt-6 flex items-center gap-5">
        <FakeQR seed="bwpay-hero-request" size={112} className="shrink-0" />
        <div className="flex-1 space-y-2.5">
          {[
            ["Network", "Solana"],
            ["Settlement", "~1.2s"],
            ["Fee", "1%"],
          ].map(([k, v]) => (
            <div
              key={k}
              className="flex items-center justify-between border-b border-line/70 pb-2 font-mono text-xs"
            >
              <span className="text-mut">{k}</span>
              <span className="text-white">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="animate-floaty absolute -right-4 -top-4 hidden items-center gap-2 rounded-full border border-line bg-card2 px-4 py-2 text-xs text-white shadow-xl sm:flex">
        <ShieldCheck className="h-3.5 w-3.5 text-sgreen" />
        Non-custodial
      </div>
      <div className="animate-floaty-slow absolute -bottom-4 -left-4 hidden items-center gap-2 rounded-full border border-line bg-card2 px-4 py-2 text-xs text-white shadow-xl sm:flex">
        <Percent className="h-3.5 w-3.5 text-sgreen" />
        1% flat fee
      </div>
    </div>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const yOrb = useTransform(scrollY, [0, 700], [0, 140]);
  const yCard = useTransform(scrollY, [0, 700], [0, -50]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="bg-grid absolute inset-0" />
      <motion.div
        style={{ y: yOrb }}
        className="animate-glow pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-sgreen/15 blur-[140px]"
      />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[480px] w-[480px] rounded-full bg-sgreen/[0.07] blur-[130px]" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-16 px-4 py-24 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-12">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-line bg-card2/70 px-4 py-1.5 backdrop-blur"
            data-testid="hero-badge"
          >
            <span className="ping-dot h-1.5 w-1.5 rounded-full bg-sgreen" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">
              Built on Solana
            </span>
          </motion.div>

          <h1 className="mt-8 text-[clamp(2.6rem,7.5vw,5.9rem)] font-bold leading-[0.98] tracking-[-0.03em]">
            <LineReveal delay={0.45}>Accept USDC.</LineReveal>
            <LineReveal delay={0.58}>Settle Instantly.</LineReveal>
            <LineReveal delay={0.71}>
              <span className="text-sgreen">Keep 99%.</span>
            </LineReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-mut sm:text-lg"
            data-testid="hero-subheadline"
          >
            The non-custodial stablecoin payment gateway for small merchants. Charge 1%
            instead of 3%. No volatility. No middlemen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToTarget("#demo")}
              data-testid="hero-cta-primary"
              className="cta-glow group inline-flex items-center gap-2.5 rounded-full bg-sgreen px-7 py-3.5 text-sm font-semibold text-black sm:text-base"
            >
              Start Accepting USDC
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToTarget("#demo")}
              data-testid="hero-cta-demo"
              className="group inline-flex items-center gap-2.5 rounded-full border border-line bg-white/[0.03] px-7 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:border-sgreen/50 hover:bg-white/[0.06] sm:text-base"
            >
              <Play className="h-4 w-4 text-sgreen" />
              View Live Demo
            </button>
            <a
              href="/BnW-Pay-Whitepaper.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whitepaper"
              className="inline-flex items-center gap-2.5 rounded-full px-5 py-3.5 text-sm font-medium text-mut transition-colors duration-300 hover:text-white sm:text-base"
            >
              <FileText className="h-4 w-4" />
              Whitepaper
            </a>
          </motion.div>
        </div>

        <motion.div
          style={{ y: yCard }}
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.85, ease: EASE }}
          className="relative [perspective:1200px]"
          onMouseMove={onMove}
          onMouseLeave={resetTilt}
          data-testid="hero-payment-card"
        >
          <motion.div style={{ rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }}>
            <PayCard />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
